import { useState } from 'react';
import { styled } from '@mui/material/styles';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import Collapse from '@mui/material/Collapse';

import Sidebar from 'src/icons/Sidebar';
import Remove from 'src/icons/Remove';
import Bookmark from 'src/icons/Bookmark';
import CustomSwitch from 'src/components/CustomSwitch';
import useFilterStore from 'src/stores/filter';
import { ROUTES_URL } from 'src/constants/routes';
import LeadStreamsService from 'src/services/LeadStreamsService';
import { shouldShowStreamInMenu } from 'src/utils/leadStreams';

import { getFeedLeadFilters } from '../utils';
import FilterItem from '../Components/FilterItem';

type FiltersProps = {
    isFavoriteMode?: boolean;
    onFavoriteModeChange?: (enabled: boolean) => void;
};

export default function Filters({ isFavoriteMode = false, onFavoriteModeChange }: FiltersProps) {
    const [open, setOpen] = useState(true);
    const location = useLocation();

    const { data: streams = [] } = useQuery({
        queryKey: LeadStreamsService.queryKey,
        queryFn: LeadStreamsService.getStreams,
        cacheTime: 5 * 60 * 1000,
        keepPreviousData: true,
        onError: () => {},
        select: (data) => data.filter(shouldShowStreamInMenu),
        staleTime: 60 * 1000,
    });

    const handleChangeOpen = () => {
        setOpen((old) => !old);
    };

    const handleClear = () => {
        useFilterStore.getState().clearFilter();
        onFavoriteModeChange?.(false);
    };

    const items = getFeedLeadFilters({ streams });
    const isFeedPage = location.pathname === ROUTES_URL.FEED;

    return (
        <Wrapper
            elevation={0}
            square={true}
        >
            <Container elevation={0}>
                {isFeedPage && (
                    <>
                        <Box
                            display="flex"
                            width="100%"
                            alignItems="center"
                            justifyContent="space-between"
                            mb={2}
                        >
                            <Typography
                                fontWeight="600"
                                variant="h4"
                            >
                                Фильтры
                            </Typography>
                            <Box
                                ml="auto"
                                title="Очистить"
                            >
                                <IconButton
                                    onClick={handleClear}
                                    aria-label="Очистить фильтры"
                                >
                                    <Remove />
                                </IconButton>
                            </Box>
                            <IconButton
                                onClick={handleChangeOpen}
                                aria-label={open ? 'Свернуть фильтры' : 'Развернуть фильтры'}
                                aria-expanded={open}
                            >
                                <Sidebar />
                            </IconButton>
                        </Box>
                        <Collapse
                            in={open}
                            timeout="auto"
                            unmountOnExit
                        >
                            {onFavoriteModeChange && (
                                <FavoriteFilterToggle
                                    checked={isFavoriteMode}
                                    onChange={onFavoriteModeChange}
                                />
                            )}
                            {items.map((item) => {
                                if (item.options[0].items.length === 0) return null;
                                return (
                                    <FilterItem
                                        key={item.title}
                                        {...item}
                                    />
                                );
                            })}
                        </Collapse>
                    </>
                )}
            </Container>
        </Wrapper>
    );
}

type FavoriteFilterToggleProps = {
    checked: boolean;
    onChange: (checked: boolean) => void;
};

const FavoriteFilterToggle = ({ checked, onChange }: FavoriteFilterToggleProps) => (
    <FavoriteToggleLabel
        sx={{
            backgroundColor: checked ? 'var(--lm-surface)' : 'transparent',
            borderColor: checked ? 'var(--lm-text)' : 'var(--lm-border)',
        }}
    >
        <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            width={24}
            height={24}
            flexShrink={0}
        >
            <Bookmark fill={checked} />
        </Box>
        <Typography
            variant="body1"
            sx={{
                flex: 1,
                fontWeight: checked ? 600 : 500,
                lineHeight: 1.2,
            }}
        >
            Только избранные
        </Typography>
        <CustomSwitch
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
            inputProps={{ 'aria-label': 'Только избранные' }}
        />
    </FavoriteToggleLabel>
);

const Wrapper = styled(Paper)(() => ({
    width: '100%',
    height: '100%',
    padding: '1rem 1rem 1rem 0',
}));

const Container = styled(Paper)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    backgroundColor: theme.palette.background.default,
    overscrollBehavior: 'contain',
    padding: theme.spacing(2),
    borderRadius: 24,
    position: 'relative',
    overflowY: 'auto',
    overflowX: 'hidden',
    maxHeight: 'calc(100vh - 120px)',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'var(--lm-canvas)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'var(--lm-scrollbar)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: 'var(--lm-muted)',
    },
}));

const FavoriteToggleLabel = styled('label')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.25),
    width: '100%',
    border: '1px solid',
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    transition: 'background-color 160ms ease, border-color 160ms ease',
    '&:hover': {
        backgroundColor: 'var(--lm-surface)',
        borderColor: '#BAC6D3',
    },
}));
